import { styled } from '@mui/material';

import { useMemo } from 'react';

import { Typography, LinearProgress, Tooltip } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';

import { InfoOutlineIcon } from '@app-velocity/icons';

export type SaveScenarioProgressProps = {
  pagesCount: number;
  savedPages: number;
  partialsCount: number;
  savedPartials: number;
  finishedCount: number;
  totalToFinish: number;
};

const LoaderContainer = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: `0 ${theme.spacing(12)}`,
}));

const GeneratingInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  svg: {
    fill: 'var(--icon-icon-secondary-color)',
  },
}));

const ProgressContainer = styled('div')(({ theme }) => ({
  width: '100%',
  margin: '10 0',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  // to handle linear progress properly
  span: {
    flex: 1,
  },
}));

const StyledPagesInfo = styled(Typography)({
  color: 'var(--color-neutral-600)',
  textAlign: 'center',
});

export const SaveScenarioProgress = ({
  pagesCount,
  savedPages,
  partialsCount,
  savedPartials,
  totalToFinish,
  finishedCount,
}: SaveScenarioProgressProps) => {
  const { t } = useI18n();

  const description = useMemo(() => {
    const totalCount = pagesCount;

    if (totalCount === 0) return null;

    if (pagesCount === 0) return t('prototypes:reading_data_from_canvas');

    return t('prototypes:waiting_for_all_to_save', {
      progress:
        pagesCount + partialsCount > 0
          ? Math.round((finishedCount / (pagesCount + partialsCount)) * 100)
          : 0,
    });
  }, [
    pagesCount,
    partialsCount,
    savedPages,
    savedPartials,
    totalToFinish,
    finishedCount,
    t,
  ]);

  const title = useMemo(() => {
    const totalCount = pagesCount;
    const totalSaved = savedPages;
    if (totalCount === 0) return t('prototypes:generating_info_10_minutes');
    if (totalCount > 0 && totalSaved < totalCount)
      return t('prototypes:generating_info_3_minutes');

    return t('prototypes:generating_info_1_minute');
  }, [
    pagesCount,
    partialsCount,
    savedPages,
    savedPartials,
    totalToFinish,
    finishedCount,
    t,
  ]);

  return (
    <LoaderContainer>
      <GeneratingInfo>
        <Typography>{title}</Typography>
        <Tooltip title={t('prototypes:loading_times_description')}>
          <InfoOutlineIcon />
        </Tooltip>
      </GeneratingInfo>
      <>
        <ProgressContainer>
          <LinearProgress
            variant={pagesCount > 0 ? 'determinate' : undefined}
            max={pagesCount + partialsCount}
            value={finishedCount}
          />
          <StyledPagesInfo variant="subtitle2">{description}</StyledPagesInfo>
        </ProgressContainer>
      </>
    </LoaderContainer>
  );
};
